import type { ApiResponse } from "apisauce"

import { request } from "../../../services/api"
import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import type {
  EventsPage,
  TrackingFilterOptions,
  TrackingPage,
  TrackingQuery,
  TrackingSummary,
  TrackingTile
} from "../types/tracking.types.ts"

const ROOT = "/esign/tracking"

export const TRACKING_PATHS = {
  list: `${ROOT}/`,
  stats: `${ROOT}/stats/`,
  summary: `${ROOT}/summary/`,
  filterOptions: `${ROOT}/filter_options/`,
  events: (envelopeId: string) => `/esign/envelopes/${envelopeId}/events/`
}

export interface Loaded<T> {
  ok: boolean
  data: T | null
}

// No response at all (a dropped connection) is a failed load, never a crash.
const loaded = <T>(response: ApiResponse<unknown> | undefined): Loaded<T> =>
  response?.ok
    ? { ok: true, data: unwrapEnvelope<T>(response.data) }
    : { ok: false, data: null }

export const getTracking = async (query: TrackingQuery) =>
  loaded<TrackingPage>(await request.api.get(TRACKING_PATHS.list, query))

export const getTrackingStats = async (
  query: Pick<TrackingQuery, "team"> = {}
) =>
  loaded<{ tiles: TrackingTile[] }>(
    await request.api.get(TRACKING_PATHS.stats, query)
  )

export const getTrackingSummary = async (query: TrackingQuery) =>
  loaded<TrackingSummary>(await request.api.get(TRACKING_PATHS.summary, query))

export const getTrackingFilterOptions = async (
  query: Pick<TrackingQuery, "team"> = {}
) =>
  loaded<TrackingFilterOptions>(
    await request.api.get(TRACKING_PATHS.filterOptions, query)
  )

export const EVENTS_PAGE_SIZE = 100

export const getEnvelopeEvents = async (envelopeId: string, page = 1) =>
  loaded<EventsPage>(
    await request.api.get(TRACKING_PATHS.events(envelopeId), {
      page,
      page_size: EVENTS_PAGE_SIZE
    })
  )
